import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import {
  getEntitlement,
  getVods,
  isTwitchSignInRequiredError,
} from '../api/rttsClient';
import ReelNameField from '../components/ReelNameField';
import DurationTierPicker from '../components/DurationTierPicker';
import type { Entitlement, ReelDurationTier, Vod } from '../api/types';
import { beginTwitchConnect } from '../auth/beginTwitchConnect';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';
import { setMockAuthState } from '../api/mockState';
import {
  STUDIO_COPY,
  STUDIO_DOCUMENT_TITLES,
  isStudioUseMock,
} from '../config';
import ReconnectTwitchCard from '../components/ReconnectTwitchCard';
import HighlightInterestProbe from '../components/HighlightInterestProbe';
import VodCard from '../components/VodCard';
import VodGridSkeleton from '../components/VodGridSkeleton';
import StudioExpectationPanel from '../components/StudioExpectationPanel';
import {
  formatVodsEntitlementIndicator,
  isGameEligibleVod,
  isReelEligibleVod,
} from '../utils/vodEligibility';
import { isReelNameValid, normalizeReelName } from '../utils/reelName';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; vods: Vod[] }
  | { kind: 'reconnect' }
  | { kind: 'error'; message: string };

/**
 * Pick-a-VOD — lists Twitch VODs; requires session + live Twitch link.
 */
const StudioVodsPage: React.FC = () => {
  useStudioDocumentTitle(STUDIO_DOCUMENT_TITLES.vods);
  const navigate = useNavigate();
  const { status, isLoggedOut, setMockState, refreshMock } = useStudioAuth();
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [generatePending, setGeneratePending] = useState(false);
  const [reelName, setReelName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [durationTier, setDurationTier] = useState<ReelDurationTier>('mid');

  const loadVods = useCallback(async () => {
    setLoadState({ kind: 'loading' });
    setSelectedId(null);
    try {
      const [vods, ent] = await Promise.all([
        getVods(),
        getEntitlement().catch(() => null),
      ]);
      setEntitlement(ent);
      setLoadState({ kind: 'ready', vods });
    } catch (error) {
      if (isTwitchSignInRequiredError(error)) {
        if (isStudioUseMock()) {
          setMockAuthState('stale_link');
          refreshMock();
        }
        setLoadState({ kind: 'reconnect' });
        return;
      }
      setLoadState({
        kind: 'error',
        message: 'Could not load your VODs. Please try again.',
      });
    }
  }, [refreshMock]);

  useEffect(() => {
    if (isLoggedOut || status === 'loading') return;
    if (status === 'stale_link') {
      setLoadState({ kind: 'reconnect' });
      return;
    }
    void loadVods();
  }, [isLoggedOut, status, loadVods]);

  const visibleVods = useMemo(() => {
    if (loadState.kind !== 'ready') return [];
    return loadState.vods.filter((vod) => isGameEligibleVod(vod));
  }, [loadState]);

  const eligibleVods = useMemo(() => {
    return visibleVods.filter((vod) => isReelEligibleVod(vod));
  }, [visibleVods]);

  const allTooShort = useMemo(() => {
    if (loadState.kind !== 'ready' || visibleVods.length === 0) return false;
    return eligibleVods.length === 0;
  }, [loadState, visibleVods, eligibleVods]);

  const unsupportedOnly = useMemo(() => {
    if (loadState.kind !== 'ready') return false;
    return loadState.vods.length > 0 && visibleVods.length === 0;
  }, [loadState, visibleVods]);

  const entitlementIndicator = useMemo(
    () => (entitlement ? formatVodsEntitlementIndicator(entitlement) : null),
    [entitlement]
  );

  if (status === 'loading') {
    return (
      <div className="flex justify-center py-16" data-testid="studio-vods-auth-loading">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (isLoggedOut) {
    return <Navigate to="/studio/connect" replace />;
  }

  const handleReconnect = () => {
    const shouldNavigate = beginTwitchConnect({ returnTo: '/studio/vods' });
    if (shouldNavigate) {
      setMockState('linked');
      void loadVods();
    }
  };

  const handleSelect = (vod: Vod) => {
    if (!isGameEligibleVod(vod) || !isReelEligibleVod(vod)) return;
    setSelectedId((prev) => {
      const next = prev === vod.vod_id ? null : vod.vod_id;
      if (next !== prev) {
        setReelName('');
        setNameTouched(false);
      }
      return next;
    });
  };

  const handleGenerate = () => {
    if (!selectedId || loadState.kind !== 'ready' || generatePending) return;
    const vod = loadState.vods.find((row) => row.vod_id === selectedId);
    if (!vod || !isGameEligibleVod(vod) || !isReelEligibleVod(vod)) {
      setSelectedId(null);
      return;
    }
    setNameTouched(true);
    const name = normalizeReelName(reelName);
    if (!isReelNameValid(name)) return;
    setGeneratePending(true);
    navigate(`/studio/generate/${encodeURIComponent(selectedId)}`, {
      state: { reelName: name, durationTier },
    });
  };

  if (loadState.kind === 'reconnect') {
    return (
      <div data-testid="studio-vods-reconnect">
        <ReconnectTwitchCard onReconnect={handleReconnect} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="studio-vods">
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="studio-page-heading">{STUDIO_COPY.vodsPageHeading}</h1>
              {loadState.kind === 'ready' || loadState.kind === 'error' ? (
                <button
                  type="button"
                  className="studio-btn-ghost text-sm px-3 py-1"
                  onClick={() => void loadVods()}
                  data-testid="studio-vods-refresh"
                >
                  {STUDIO_COPY.vodsRefreshCta}
                </button>
              ) : null}
            </div>
            <StudioExpectationPanel />
            <p
              className="studio-vods-retention"
              data-testid="studio-vods-retention"
            >
              {STUDIO_COPY.vodsRetentionLine}
            </p>
            {entitlement?.recap_only !== false ? (
              <div
                className="studio-recap-lock"
                data-testid="studio-recap-lock"
              >
                <p className="font-medium text-[var(--studio-accent-bright)]">
                  {STUDIO_COPY.recapLockLabel}
                </p>
                <p className="text-sm text-[var(--studio-muted)]">
                  {STUDIO_COPY.recapLockBlurb}
                </p>
              </div>
            ) : null}
            <HighlightInterestProbe visible={entitlement?.recap_only !== false} />
          </div>
          {entitlementIndicator ? (
            <div
              className="flex flex-wrap items-center gap-2 text-sm"
              data-testid="studio-vods-entitlement"
            >
              <span
                className="rounded-full border border-[var(--studio-border)] bg-[var(--studio-bg-elevated)] px-3 py-1 text-[var(--studio-text)]"
                data-testid="studio-vods-entitlement-badge"
              >
                {entitlementIndicator.text}
              </span>
              {entitlementIndicator.showUpgrade ? (
                <Link
                  to="/studio/upgrade"
                  className="studio-btn-ghost no-underline text-sm px-3 py-1"
                  data-testid="studio-vods-upgrade-cta"
                >
                  {STUDIO_COPY.upgradeCta}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {loadState.kind === 'loading' ? <VodGridSkeleton /> : null}

      {loadState.kind === 'error' ? (
        <div
          className="studio-panel space-y-3"
          role="alert"
          data-testid="studio-vods-error"
        >
          <p>{loadState.message}</p>
          <button type="button" className="studio-btn-ghost" onClick={() => void loadVods()}>
            {STUDIO_COPY.tryAgain}
          </button>
        </div>
      ) : null}

      {loadState.kind === 'ready' && loadState.vods.length === 0 ? (
        <div
          className="studio-panel space-y-3"
          data-testid="studio-vods-empty"
        >
          <h2 className="studio-card-title">{STUDIO_COPY.vodsEmptyHeadline}</h2>
          <p className="studio-page-kicker">
            {STUDIO_COPY.vodsEmptyBody}
          </p>
        </div>
      ) : null}

      {loadState.kind === 'ready' && unsupportedOnly ? (
        <div
          className="studio-panel space-y-3"
          data-testid="studio-vods-unsupported"
        >
          <h2 className="studio-card-title">{STUDIO_COPY.vodsUnsupportedHeadline}</h2>
          <p className="studio-page-kicker">{STUDIO_COPY.vodsUnsupportedBody}</p>
        </div>
      ) : null}

      {loadState.kind === 'ready' && visibleVods.length > 0 ? (
        <>
          {allTooShort ? (
            <div
              className="studio-panel space-y-3"
              data-testid="studio-vods-all-too-short"
              role="status"
            >
              <h2 className="text-xl font-semibold">
                {STUDIO_COPY.vodsAllTooShortHeadline}
              </h2>
              <p className="text-[var(--studio-muted)]">{STUDIO_COPY.vodsAllTooShortBody}</p>
            </div>
          ) : null}

          <div
            className="studio-vods-grid"
            data-testid="studio-vods-grid"
          >
            {visibleVods.map((vod) => (
              <VodCard
                key={vod.vod_id}
                vod={vod}
                selected={selectedId === vod.vod_id}
                onSelect={handleSelect}
              />
            ))}
          </div>

          {selectedId && !allTooShort ? (
            <div
              className="studio-generate-bar studio-fade-up"
              data-testid="studio-generate-bar"
            >
              <div className="studio-generate-bar-name">
                <ReelNameField
                  value={reelName}
                  onChange={setReelName}
                  showError={nameTouched}
                  disabled={generatePending}
                />
                {entitlement?.duration_tiers_enabled ? (
                  <DurationTierPicker
                    value={durationTier}
                    onChange={setDurationTier}
                    disabled={generatePending}
                  />
                ) : null}
              </div>
              <button
                type="button"
                className="studio-btn-primary shadow-lg"
                onClick={handleGenerate}
                disabled={generatePending || !isReelNameValid(reelName)}
                aria-busy={generatePending}
                data-testid="studio-generate-cta"
              >
                {generatePending ? 'Starting…' : 'Generate reel'}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default StudioVodsPage;
